import { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { Button, Text, theme, Icon } from '@socrait/react';
import { QueryStatus } from '@tanstack/react-query';

import { AccordionBase, useAccordionState, type CopyText } from '~/components/Accordion';
import { ErrorBox } from '~/components/ErrorBox';
import { MomentsResponse } from '~/services/review/useMomentsQuery';
import { getMeetingContentState } from '~/pages/review/utils/meetingContentState';

interface ClassroomMomentsProps {
  data?: MomentsResponse | null;
  status?: QueryStatus | null;
  activeMeetingId?: string;
}

export const ClassroomMoments = ({ data, status, activeMeetingId }: ClassroomMomentsProps) => {
  const { isCollapsed, accordionState, handleToggleCollapse } = useAccordionState();

  const handleButtonClick = () => {
    handleToggleCollapse(!isCollapsed);
  };

  const contentState = getMeetingContentState(data?.meetings, activeMeetingId, data?.status);
  const moments = contentState.kind === 'content' ? contentState.meeting.moments : [];
  const topMoment = moments[0];

  const copyText = useMemo((): CopyText => {
    const quote = topMoment?.quote || '';
    return {
      plain: quote,
      html: quote,
    };
  }, [topMoment]);

  const viewButton = (
    <Button
      name="View"
      onPress={handleButtonClick}
      containerStyle={[styles.viewButton, !isCollapsed && styles.viewButtonExpanded]}
      fontStyle={styles.viewButtonText}
      icon={!isCollapsed ? 'topArrow' : undefined}
    />
  );

  const content = useMemo(() => {
    if (!topMoment) return null;
    return (
      <View style={styles.momentRow}>
        {topMoment.context ? <Text style={styles.contextText}>{topMoment.context}</Text> : null}
        <Text style={styles.quoteText}>“{topMoment.quote}”</Text>
      </View>
    );
  }, [topMoment]);

  if (!topMoment) {
    return null;
  }

  return (
    <View style={styles.container}>
      {status === 'error' ? (
        <ErrorBox text="Could not load classroom moments. Please try refreshing the page." />
      ) : (
        <AccordionBase
          title="**You had a classroom moment!**"
          icon={<Icon type="heart" fill={theme.colors.navy} />}
          rightControls={viewButton}
          content={content}
          isCollapsed={isCollapsed}
          onToggleCollapse={handleToggleCollapse}
          state={accordionState}
          autoExpand={false}
          containerStyle={styles.accordionContainer}
          noContentBorder={true}
          copyText={copyText}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    gap: 10,
  },
  accordionContainer: {
    backgroundColor: theme.colors.veryLightTeal,
    borderWidth: 0,
  },
  viewButton: {
    borderRadius: theme.borderRadius.xlarge,
  },
  viewButtonText: {
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.bold,
  },
  viewButtonExpanded: {
    backgroundColor: theme.colors.transparent,
    borderWidth: 1,
  },
  momentRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.small,
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.large,
    paddingHorizontal: theme.spacing.large,
    paddingVertical: theme.spacing.medium,
  },
  quoteText: {
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.bold,
  },
  contextText: {
    fontSize: theme.fontSizes.xsmall,
    fontStyle: 'italic',
  },
});
